import type { SideMenuRootProps } from '@halo/ui-react';
import { SideMenuRoot, SideMenuAside } from '@halo/ui-react';
import {
  Box,
  Center,
  Flex,
  HStack,
  Separator,
  Show,
  Skeleton,
  SkeletonCircle,
  Stack,
  Text,
} from '@chakra-ui/react';
import { BackIcon, FilterIcon, LockIcon } from '@halo/ui-chat-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MyTrans } from '@halo/ui-react';
import { NetworkErrorContent, type NetworkErrorContentProps } from '../../ErrorBar';

interface ChatSidebarProps extends SideMenuRootProps, NetworkErrorContentProps {
  show?: boolean;
  children: ReactNode;
  connecting?: boolean;
  loading?: boolean;
  showCollapseChat?: boolean;
  onShowCollapseChat?: (show: boolean) => void;
}

function ChatSidebar({
  children,
  show,
  connecting,
  loading,
  packageError,
  onRetry,
  showCollapseChat,
  onShowCollapseChat,
  ...props
}: ChatSidebarProps) {
  const { t } = useTranslation();
  return (
    <Box className="ChatSidebarContainer" h="full" py={2.5}>
      <SideMenuRoot
        className="ChatSidebar"
        as="aside"
        h="full"
        bg="bg.panel"
        rounded="lg"
        {...props}
      >
        <SideMenuAside className="ChatSidebarHeader" as="header" p={2}>
          {showCollapseChat ? (
            <HStack gap="2" cursor="pointer" onClick={() => onShowCollapseChat?.(false)}>
              <BackIcon style={{ color: 'var(--colors-fg)' }} />
              <Text fontWeight="semibold" fontSize="md">
                {t('chatList.collapseChat')}
              </Text>
            </HStack>
          ) : (
            <HStack gap="2">
              <FilterIcon style={{ color: 'var(--colors-fg)' }} />
              <Text fontWeight="semibold" fontSize="md">
                {connecting
                  ? t('tips.connecting')
                  : loading
                    ? t('tips.loading')
                    : t('nav.messages')}
              </Text>
            </HStack>
          )}
        </SideMenuAside>
        {packageError && (
          <SideMenuAside className="ChatSidebarHeader" as="header">
            <NetworkErrorContent packageError={packageError} onRetry={onRetry} />
          </SideMenuAside>
        )}
        <Show when={show} fallback={<LoadingContent />}>
          {children}
        </Show>
        <Separator borderColor="border.subtle" />
        <SideMenuAside className="ChatSidebarFooter" as="footer" py={5} textAlign="center">
          <Flex
            fontSize="xs"
            gap="1"
            color="fg.muted"
            align="center"
            justify="center"
            flexWrap="wrap"
          >
            <Center fontSize="sm" className="shake">
              <LockIcon />
            </Center>
            <Text as="span">
              <MyTrans
                i18nKey="general.encryptedTips"
                components={{ component: <Text as="span" color="brand.100" /> }}
              />
            </Text>
          </Flex>
        </SideMenuAside>
      </SideMenuRoot>
    </Box>
  );
}

function LoadingContent() {
  return (
    <Stack h="full" w="full" bg="bg.panel" gap="5" p="3.5">
      {Array.from({ length: 4 }).map((_, index) => (
        <Flex key={index} gap="2">
          <SkeletonCircle size="12" />
          <Stack flex="1">
            <Skeleton height="5" />
            <Skeleton height="7" />
          </Stack>
        </Flex>
      ))}
    </Stack>
  );
}

const MemoizedChatSidebar = memo(ChatSidebar);

export { MemoizedChatSidebar as ChatSidebar };
